import { StationRow } from '../StationRow';
import { AreaStationsTableProps } from './AreaStationTable.types';

export const AreaStationsTable = ({
  stationsData,
  fuelTypes,
  isMobile,
  sortBy,
  selectedBrand,
  tableClass,
  scrollRef,
  hideSales = false
}: AreaStationsTableProps) => {
  return (
    <div className="stations-table-body-wrapper" ref={scrollRef}>
      <table className={`regional-stations-table ${tableClass ?? ''} ${isMobile ? 'mobile-table' : ''}`}>
        <colgroup>
          <col className="stations-col-brand" />
          <col className="stations-col-unicode" />
          <col className="stations-col-address" />
          <col className="stations-col-photo" />
          <col className="stations-col-date" />
          <col className="stations-col-influence" />
          <col className="stations-col-distance" />
          <col className="stations-col-location" />
          {!hideSales && <col className="stations-col-sales" />}
          {fuelTypes.map(fuel => (
            <col key={fuel} className="stations-col-fuel" />
          ))}
        </colgroup>
        <tbody>
          {stationsData?.stations && (
            <>
              {/* Alphabetical mode */}
              {sortBy === 'alphabetical' &&
                stationsData.stations
                  .filter(s => !selectedBrand || s.brand === selectedBrand || s.brand === 'ОККО')
                  .map(station => (
                    <StationRow
                      key={`alpha-${station.unicode}`}
                      station={station}
                      isOkko={station.brand === 'ОККО'}
                      fuelTypes={fuelTypes}
                      rowType="alphabetical"
                      hideSales={hideSales}
                    />
                  ))}

              {/* Bindings mode */}
              {sortBy === 'bindings' &&
                (() => {
                  const renderedOkko = new Set<string>();
                  const rows: JSX.Element[] = [];

                  const okkoStations = stationsData.stations.filter(s => s.brand === 'ОККО');
                  const competitors = stationsData.stations.filter(s => s.brand !== 'ОККО');

                  // Bound OKKO + bound competitors
                  okkoStations.forEach(okko => {
                    const boundCompetitors = competitors.filter(c => {
                      if (!c.binded_to) return false;
                      if (selectedBrand && c.brand !== selectedBrand) return false;
                      return c.binded_to.some(b => b.okko_station_unicode === okko.unicode);
                    });

                    if (boundCompetitors.length > 0 && !renderedOkko.has(okko.unicode)) {
                      rows.push(
                        <StationRow
                          key={`okko-${okko.unicode}`}
                          station={okko}
                          isOkko={true}
                          fuelTypes={fuelTypes}
                          rowType="bound-okko"
                          hideSales={hideSales}
                        />
                      );
                      renderedOkko.add(okko.unicode);

                      boundCompetitors.forEach(c => {
                        const binding = c.binded_to!.find(b => b.okko_station_unicode === okko.unicode);
                        rows.push(
                          <StationRow
                            key={`comp-${c.unicode}-bound-to-${okko.unicode}`}
                            station={c}
                            binding={binding}
                            isOkko={false}
                            fuelTypes={fuelTypes}
                            rowType="bound-competitor"
                            hideSales={hideSales}
                          />
                        );
                      });
                    }
                  });

                  // Unbound OKKO stations
                  okkoStations.forEach(okko => {
                    const hasBound = competitors.some(c => {
                      if (!c.binded_to) return false;
                      return c.binded_to.some(b => b.okko_station_unicode === okko.unicode);
                    });
                    if (!hasBound && !renderedOkko.has(okko.unicode)) {
                      rows.push(
                        <StationRow
                          key={`unbound-okko-${okko.unicode}`}
                          station={okko}
                          isOkko={true}
                          fuelTypes={fuelTypes}
                          rowType="unbound-okko"
                          hideSales={hideSales}
                        />
                      );
                      renderedOkko.add(okko.unicode);
                    }
                  });

                  // Orphaned competitors
                  competitors.forEach(c => {
                    if (!c.binded_to) return;
                    if (selectedBrand && c.brand !== selectedBrand) return;
                    const hasOrphaned = c.binded_to.some(b => !okkoStations.some(okko => okko.unicode === b.okko_station_unicode));
                    if (hasOrphaned) {
                      rows.push(
                        <StationRow
                          key={`orphaned-${c.unicode}`}
                          station={c}
                          isOkko={false}
                          fuelTypes={fuelTypes}
                          rowType="orphaned-competitor"
                          hideSales={hideSales}
                        />
                      );
                    }
                  });

                  // Unbound competitors
                  competitors
                    .filter(c => !c.binded_to && (!selectedBrand || c.brand === selectedBrand))
                    .forEach(c => {
                      rows.push(
                        <StationRow
                          key={`unbound-comp-${c.unicode}`}
                          station={c}
                          isOkko={false}
                          fuelTypes={fuelTypes}
                          rowType="unbound-competitor"
                          hideSales={hideSales}
                        />
                      );
                    });

                  return rows;
                })()}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};
